import { getStay22Aid } from '@/config/bookingExperience';
import {
  DEFAULT_STAY22_ALLEZ_PROVIDER,
  type Stay22AllezProvider,
} from '@/lib/booking/stay22AllezProviders';

export type { Stay22AllezProvider } from '@/lib/booking/stay22AllezProviders';

export interface BuildStay22AllezUrlParams {
  aid?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** Allez endpoint segment; defaults to `roam` (ML vendor pick). */
  provider?: Stay22AllezProvider;
  /** Hotel name for Stay22 fuzzy property matching (sent as `hotelname`). */
  hotelName?: string | null;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
  campaign: string;
}

function getStay22AllezBase(provider: Stay22AllezProvider = DEFAULT_STAY22_ALLEZ_PROVIDER): string {
  return `https://www.stay22.com/allez/${provider}`;
}

/**
 * Builds a Stay22 Allez deeplink with affiliate tracking and campaign attribution.
 */
export function buildStay22AllezUrl(params: BuildStay22AllezUrlParams): string | null {
  const aid = (params.aid ?? getStay22Aid())?.trim();
  const campaign = params.campaign?.trim();

  if (!aid || !campaign) {
    return null;
  }

  const lat = params.lat;
  const lng = params.lng;
  const address = params.address?.trim();
  const hotelName = params.hotelName?.trim();

  if ((lat == null || lng == null) && !address) {
    return null;
  }

  const provider = params.provider ?? DEFAULT_STAY22_ALLEZ_PROVIDER;
  const url = new URL(getStay22AllezBase(provider));
  url.searchParams.set('aid', aid);
  url.searchParams.set('campaign', campaign);

  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    url.searchParams.set('lat', String(lat));
    url.searchParams.set('lng', String(lng));
  }
  if (address) {
    url.searchParams.set('address', address);
  }
  // Vrbo is vacation-rental focused; hotel name matching confuses city/area search.
  if (hotelName && provider !== 'vrbo') {
    url.searchParams.set('hotelname', hotelName);
  }

  if (params.checkIn) {
    url.searchParams.set('checkin', params.checkIn);
  }
  if (params.checkOut) {
    url.searchParams.set('checkout', params.checkOut);
  }
  if (params.adults != null && params.adults > 0) {
    url.searchParams.set('adults', String(params.adults));
  }
  if (params.children != null && params.children > 0) {
    url.searchParams.set('children', String(params.children));
  }

  return url.toString();
}
